import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { parse as qs } from 'qs'
import moment from 'moment-timezone'
import Form from 'components/form-components/form/form'
import { actions, track } from 'react-redux-form'
import { validators } from 'utils/form'
import FormGroup from 'components/form-components/form-group'
import Attachments from 'components/attachments/attachments'
import AgreeTos from 'components/agree-tos'
import FormErrors from 'components/form-components/form-errors'
import getCookie from 'utils/cookie'
import { saveApplication } from 'careers/actions/application'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { mapLanguageToComponentProps } from 'i18n'
import LinkListInput from 'components/link-list-input'
import AdditionalFieldsForm from 'components/additional-fields-form'
import { logEvent, EVENT_ACTIONS } from 'actions/audit-trail'
import { LabelledInput } from 'components/form-components/styled-text-input/with-label'
import ThirdPartyScripts from 'careers/components/third-party-scripts'
import PhoneInput from 'components/phone-input'
import { Body, H3, Label } from 'components/styled/typography'
import { Button } from 'components/styled/button'
import ActionBarWrapper from 'components/action-bar-wrapper'
import { PageContent } from 'components/styled/page/page-content'
import { getPreviewActionBarRoot } from 'components/styled/page/preview-app-content'
import { LabelledTextArea } from 'components/form-components/styled-text-area/with-label'
import { addError } from 'actions'

const propTypes = {
  i18n: PropTypes.shape({
    jobAdContainer: PropTypes.object.isRequired,
    jobAdApplication: PropTypes.object.isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  jobAd: PropTypes.shape({
    _id: PropTypes.string,
    language: PropTypes.string,
    slugs: PropTypes.array,
    formSetup: PropTypes.shape({
      attachments: PropTypes.object,
      coverLetter: PropTypes.object,
      email: PropTypes.object,
      links: PropTypes.object,
      name: PropTypes.object,
      phoneNumber: PropTypes.object,
      additionalFormFields: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          type: PropTypes.string,
          required: PropTypes.bool,
        })
      ),
      city: PropTypes.object,
    }),
    questions: PropTypes.array,
    privacyPolicy: PropTypes.string,
  }).isRequired,
  preview: PropTypes.bool,
  application: PropTypes.shape({}).isRequired,
  company: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired,
  context: PropTypes.string,
  submitApplication: PropTypes.func,
}

const defaultProps = {
  preview: false,
  context: 'careers',
  submitApplication: undefined,
}

const mapStateToProps = state => ({
  application: state.application,
  jobAd: state.jobAd,
})

const { isRequired, isEmail } = validators

const JobAdApply = ({
  i18n,
  dispatch,
  jobAd,
  preview,
  application,
  company,
  context,
  submitApplication,
}) => {
  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()

  const [applicationForm, setApplicationForm] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const [invalidFields, setInvalidFields] = useState([])
  const [linkedInPictureUrl, setLinkedInPictureUrl] = useState(null)
  const [linkedInName, setLinkedInName] = useState(null)
  const [linkedInAuthorizationCode, setLinkedInAuthorizationCode] =
    useState(null)
  const [linkedInError, setLinkedInError] = useState(null)
  const [clearState, setClearState] = useState(false)

  useEffect(() => {
    return () => {
      if (clearState) {
        dispatch(actions.reset('application'))
        dispatch(actions.change('application.email', application.email))
      }
    }
  }, [clearState])

  useEffect(() => {
    let query = null
    if (context === 'careers') {
      let { search } = location
      if (search[0] === '?') {
        search = search.substring(1)
      }
      query = qs(search)
    }

    const code = query ? query.linkedInCode : undefined
    const error = query ? query.linkedInError : undefined
    setLinkedInAuthorizationCode(code)
    setLinkedInError(error)

    dispatch(
      logEvent({
        ec: company._id,
        ea: EVENT_ACTIONS.jobAdApplyView,
      })
    )

    if (code || error) {
      navigate({
        pathname: location.pathname,
        search: '',
      })
    }
  }, [])

  const onSubmitApplication = () => {
    dispatch(actions.submit('application'))

    if (applicationForm && applicationForm.valid) {
      const visibleFields = jobAd.additionalFormFields.filter(
        field => field.fieldSetup.visible
      )

      setLoading(true)
      const data =
        jobAd.questions.length === 0 || !jobAd.formSetup.questions.visible
          ? { ...application, jobAd, completed: true }
          : { ...application, jobAd }

      if (linkedInName) {
        data.linkedIn = {
          pictureUrl: linkedInPictureUrl,
        }
      }

      const referrer = getCookie('referrer')
      if (visibleFields.length > 0) {
        document.additionalFieldsForm.submit()
      }
      if (invalidFields.length > 0) {
        setLoading(false)
        dispatch(
          actions.setSubmitFailed('application.additionalFieldResponses')
        )
      } else {
        dispatch(saveApplication(data, referrer))
          .then(() => {
            setLoading(false)
            if (
              jobAd.questions.length > 0 &&
              jobAd.formSetup.questions.visible
            ) {
              navigate(
                `/careers/${params.language}/${params.companySlug}/${params.adSlug}/interview`
              )
            } else {
              navigate(
                `/careers/${params.language}/${params.companySlug}/${params.adSlug}/thankyou`
              )
              setClearState(true)
            }
          })
          .catch(err => {
            setLoading(false)
            dispatch(addError(err))
          })
      }
    } else {
      submitAdditionalFieldsForm()
    }
  }

  const onChange = (model, value) => {
    if (model === 'additionalFieldResponse') {
      if (
        application.additionalFieldResponses.find(
          f => f.fieldId === value.fieldId
        )
      ) {
        dispatch(
          actions.change(
            track('application.additionalFieldResponses', {
              fieldId: value.fieldId,
            }),
            value
          )
        )
      } else {
        dispatch(actions.push('application.additionalFieldResponses', value))
      }
    } else {
      dispatch(actions.change(model, value))
    }
    return true
  }

  const onLinkedInAuthorizationRequest = csrfToken => {
    window.sessionStorage.setItem('careersLang', jobAd.language)
    window.sessionStorage.setItem('careersCompany', company.slug)
    window.sessionStorage.setItem(
      'careersJobAd',
      jobAd.slugs[jobAd.slugs.length - 1]
    )
    window.sessionStorage.setItem('careersCsrf', csrfToken)
  }

  const onLinkedInProfileData = linkedInData => {
    const { name = {}, email = {} } = jobAd.formSetup

    setLinkedInPictureUrl(linkedInData.profilePicture)
    setLinkedInName(`${linkedInData.firstName} ${linkedInData.lastName}`)

    if (email.visible) {
      dispatch(actions.change('application.email', linkedInData.email))
    }

    if (name.visible) {
      dispatch(
        actions.change(
          'application.name',
          `${linkedInData.firstName} ${linkedInData.lastName}`
        )
      )
    }
  }

  const submitAdditionalFieldsForm = () => {
    const visibleFields = jobAd.additionalFormFields.filter(
      field => field.fieldSetup.visible
    )
    if (visibleFields.length > 0) {
      document.additionalFieldsForm.submit()
    }
  }

  const checkAdditionalFieldsValidity = fields => {
    Object.keys(fields).forEach(key => {
      const isInInvalidFields = invalidFields.indexOf(key) > -1
      const validField = fields[key].valid
      if (!validField && !isInInvalidFields) {
        setInvalidFields([...invalidFields, key])
      } else if (validField && isInInvalidFields) {
        const filtered = invalidFields.filter(id => id !== key)
        setInvalidFields(filtered)
      }
    })
  }

  const hasValidation = field => {
    if (context === 'careers' && field.required) {
      return true
    }
    return false
  }

  if (jobAd && jobAd._id) {
    const {
      name = {},
      coverLetter = {},
      email = {},
      attachments = {},
      phoneNumber = {},
      links = {},
      city = {},
    } = jobAd.formSetup

    const visibleFields = jobAd.additionalFormFields.filter(
      field => field.fieldSetup.visible
    )

    const formattedDateTime =
      jobAd.endTime &&
      `${moment(jobAd.endTime).format('L')} ${moment(jobAd.endTime).format('LT')}`

    const showContinueIcon =
      jobAd.questions.length > 0 && jobAd.formSetup.questions.visible

    return (
      <>
        <Form
          className="job-ad-apply"
          id="application-form"
          name="applicationForm"
          model="application"
          onUpdate={form => {
            setApplicationForm(form && form.$form)
          }}
        >
          <PageContent size="small" mt="s24">
            <H3>{i18n.jobAdApplication.legendBasicInfo}</H3>
            <Body>{i18n.jobAdApplication.required}</Body>
            {name.visible ? (
              <FormGroup model="application.name">
                <LabelledInput
                  title={i18n.jobAdApplication.name}
                  isRequired={hasValidation(name)}
                  model="application.name"
                  validators={hasValidation(name) ? { isRequired } : null}
                  validateOn="change"
                  type="text"
                  placeholder={i18n.jobAdApplication.namePlaceholder}
                />
              </FormGroup>
            ) : null}
            {email.visible ? (
              <FormGroup model="application.email">
                <LabelledInput
                  title={i18n.jobAdApplication.email}
                  isRequired={hasValidation(email)}
                  model="application.email"
                  validators={
                    hasValidation(email) ? { isRequired, isEmail } : { isEmail }
                  }
                  validateOn="change"
                  type="email"
                  placeholder={i18n.jobAdApplication.emailPlaceholder}
                />
              </FormGroup>
            ) : null}
            {phoneNumber.visible ? (
              <FormGroup model="application.phone" name="application.phone">
                <Label color="grey90">{`${i18n.jobAdApplication.phone}${
                  phoneNumber.required ? '*' : ''
                }`}</Label>
                <PhoneInput
                  model="application.phone"
                  isRequired={hasValidation(phoneNumber)}
                />
                <FormErrors model="application.phone" />
              </FormGroup>
            ) : null}
            {city.visible ? (
              <FormGroup model="application.city">
                <LabelledInput
                  title={i18n.jobAdApplication.city}
                  isRequired={hasValidation(city)}
                  model="application.city"
                  validators={hasValidation(city) ? { isRequired } : null}
                  validateOn="change"
                  type="text"
                  placeholder={i18n.jobAdApplication.cityPlaceholder}
                />
              </FormGroup>
            ) : null}
            {links.visible ? (
              <FormGroup model="application.links">
                <LinkListInput
                  model="application.links"
                  setup={links}
                  readOnly={preview}
                  placeholder={
                    i18n.jobAdApplication.links + (links.required ? '*' : '')
                  }
                  isRequired={hasValidation(links)}
                />
              </FormGroup>
            ) : null}
            {attachments.visible ? (
              <Attachments
                model="application.attachments"
                description={attachments.description}
                limit={5}
                readOnly={preview}
                isRequired={hasValidation(attachments)}
              />
            ) : null}
            {coverLetter.visible ? (
              <FormGroup model="application.coverLetter">
                <LabelledTextArea
                  title={i18n.jobAdApplication.coverLetter}
                  isRequired={hasValidation(coverLetter)}
                  model="application.coverLetter"
                  readOnly={preview}
                  validators={
                    hasValidation(coverLetter) ? { isRequired } : null
                  }
                  rows={10}
                  validateOn="change"
                  type="text"
                  placeholder={i18n.jobAdApplication.introduction}
                />
              </FormGroup>
            ) : null}
            {visibleFields.length > 0 && (
              <FormGroup model="application.additionalFieldResponses">
                <AdditionalFieldsForm
                  legend={i18n.jobAdApplication.legendAdditionalFields}
                  additionalFormFields={visibleFields}
                  responses={application.additionalFieldResponses}
                  changeAction={(model, value) => {
                    onChange(model, value)
                  }}
                  isFormValid={fields => checkAdditionalFieldsValidity(fields)}
                />
              </FormGroup>
            )}
            <FormGroup model="application.agreeTOS">
              <AgreeTos
                model="application.agreeTOS"
                agreed={application.agreeTOS}
                onChange={value => onChange('application.agreeTOS', value)}
                privacyLink={jobAd.privacyPolicy}
                validators={context === 'admin' ? null : { isRequired }}
              />
            </FormGroup>
          </PageContent>
          <ActionBarWrapper
            getActionBarRoot={
              context === 'admin' ? getPreviewActionBarRoot : undefined
            }
            leftSideComponent={
              formattedDateTime && (
                <Body fontWeight={600}>
                  {i18n.jobAdContainer.endTime(formattedDateTime)}
                </Body>
              )
            }
            actions={
              <Button
                text={
                  jobAd.questions.length > 0 &&
                  jobAd.formSetup.questions.visible
                    ? i18n.jobAdApplication.continue
                    : i18n.jobAdApplication.submit
                }
                event={{
                  locationId: 'jobAdApplyPage',
                  id: 'applicationSubmitButton',
                  properties: {
                    companyId: company._id,
                    companyName: company.name,
                    language: jobAd && jobAd.language,
                  },
                }}
                iconRight={showContinueIcon ? 'east' : undefined}
                icon={!showContinueIcon ? 'save' : undefined}
                onClick={
                  context === 'careers'
                    ? onSubmitApplication
                    : submitApplication
                }
                loading={loading}
              />
            }
          />
          {context === 'careers' && <ThirdPartyScripts />}
        </Form>
      </>
    )
  }
  return null
}

JobAdApply.propTypes = propTypes
JobAdApply.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(JobAdApply)
